<template>
  <div class="space-y-6">
    <CreatorPlanLimitPrompt :open="showLimitPrompt" :resource="limitResource" @close="showLimitPrompt = false" />
    <div
      v-if="errorMessage"
      class="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm"
      :class="showStripeIntegrationLink ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-red-200 bg-red-50 text-red-700'"
    >
      <span>{{ errorMessage }}</span>
      <router-link
        v-if="showStripeIntegrationLink"
        to="/creator/app-settings/integrations?provider=stripe"
        class="inline-flex items-center gap-1.5 rounded-lg bg-[#2D8CFF] px-3 py-2 text-xs font-bold text-white transition hover:bg-blue-600"
      >
        <LinkIcon class="h-4 w-4" />
        Connect Stripe
      </router-link>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="metric in summaryMetrics"
        :key="metric.label"
        class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm"
      >
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">{{ metric.label }}</p>
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-[#2D8CFF]">
            <component :is="metric.icon" class="h-5 w-5" />
          </div>
        </div>
        <p class="mt-3 text-2xl font-semibold text-zinc-900">{{ metric.value }}</p>
        <p class="mt-1 text-xs text-zinc-500">{{ metric.detail }}</p>
      </article>
    </div>

    <div class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-zinc-900">Checkout payment preferences</h3>
          <p class="mt-1 text-sm text-zinc-500">Choose the payment option your checkout uses by default.</p>
        </div>
        <button
          type="button"
          class="inline-flex h-10 items-center rounded-md border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isSaving || isLoading"
          @click="saveSettings"
        >
          {{ isSaving ? 'Saving...' : 'Save Settings' }}
        </button>
      </div>

      <div class="mt-5 grid gap-4 lg:grid-cols-3">
        <button type="button" class="relative flex min-h-[220px] flex-col rounded-2xl border-2 p-5 text-left transition focus:outline-none focus:ring-2 focus:ring-[#2D8CFF]/30" :class="providerCardClass('platforma_payments')" :disabled="isLoading || isSaving" @click="selectProvider('platforma_payments')">
          <div class="flex items-start justify-between gap-4"><div class="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50"><img :src="platformaStackLogo" alt="" class="h-9 w-9 object-contain"></div><CheckCircleIcon v-if="form.defaultProvider === 'platforma_payments'" class="h-6 w-6 text-emerald-500" /></div>
          <h4 class="mt-5 text-base font-semibold text-zinc-900">Platforma Payments</h4>
          <p class="mt-1 flex-1 text-sm leading-6 text-zinc-500">Accept member payments without connecting another Stripe account.</p>
          <div class="mt-4 flex flex-wrap gap-2"><StatusBadge :active="form.defaultProvider === 'platforma_payments'" active-label="Default" inactive-label="Available" /><StatusBadge :active="true" active-label="Enabled" /><StatusBadge :active="platformaActive" active-label="Active" inactive-label="Setup required" /></div>
        </button>

        <button type="button" class="relative flex min-h-[220px] flex-col rounded-2xl border-2 p-5 text-left transition focus:outline-none focus:ring-2 focus:ring-[#2D8CFF]/30" :class="providerCardClass('creator_stripe')" :disabled="isLoading || isSaving" @click="selectProvider('creator_stripe')">
          <div class="flex items-start justify-between gap-4"><div class="flex h-12 min-w-12 items-center justify-center rounded-xl bg-violet-50 px-2"><img :src="stripeLogoSrc" alt="Stripe" class="h-8 w-auto object-contain"></div><CheckCircleIcon v-if="form.defaultProvider === 'creator_stripe'" class="h-6 w-6 text-emerald-500" /><LockClosedIcon v-else-if="!stripePlanAllowed" class="h-6 w-6 text-zinc-400" /></div>
          <h4 class="mt-5 text-base font-semibold" :class="stripePlanAllowed ? 'text-zinc-900' : 'text-zinc-500'">Connected Stripe</h4>
          <p class="mt-1 flex-1 text-sm leading-6 text-zinc-500">Route checkout payments through one of your connected Stripe accounts.</p>
          <div class="mt-4 flex flex-wrap gap-2"><StatusBadge :active="form.defaultProvider === 'creator_stripe'" active-label="Default" :inactive-label="stripePlanAllowed ? 'Not default' : 'Upgrade required'" /><StatusBadge :active="stripeEnabled" active-label="Enabled" :inactive-label="stripePlanAllowed ? 'Not enabled' : 'Unavailable'" /><StatusBadge :active="stripeActive" active-label="Active" :inactive-label="stripePlanAllowed ? 'Connect account' : 'Paid plan'" /></div>
        </button>

        <div class="flex min-h-[220px] flex-col rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 p-5 text-left opacity-75">
          <div class="flex items-start justify-between gap-4"><div class="flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-200"><img :src="bitcoinIcon" alt="" class="h-8 w-8 grayscale opacity-50"></div><LockClosedIcon class="h-6 w-6 text-zinc-400" /></div>
          <h4 class="mt-5 text-base font-semibold text-zinc-500">Bitcoin</h4>
          <p class="mt-1 flex-1 text-sm leading-6 text-zinc-500">Bitcoin checkout will appear here when it becomes available for creator payments.</p>
          <div class="mt-4 flex flex-wrap gap-2"><StatusBadge :active="false" inactive-label="Not default" /><StatusBadge :active="false" inactive-label="Not enabled" /><StatusBadge :active="false" inactive-label="Not available" /></div>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-zinc-50 px-4 py-3"><p class="text-sm text-zinc-600">{{ selectionMessage }}</p><router-link v-if="form.defaultProvider !== 'creator_stripe' || !stripeActive" to="/creator/app-settings/integrations?provider=stripe" class="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D8CFF] hover:text-blue-700"><LinkIcon class="h-4 w-4" />Manage integrations</router-link></div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, reactive, ref } from 'vue';
import { BanknotesIcon, CheckCircleIcon, CreditCardIcon, LinkIcon, LockClosedIcon, ReceiptPercentIcon, WalletIcon } from '@heroicons/vue/24/outline';
import CreatorPlanLimitPrompt from '@/Components/CreatorPlanLimitPrompt.vue';
import platformaStackLogo from '@/assets/media/images/platformalogostack.png';
import stripeLogoSrc from '@/assets/media/images/Stripe_Logo.svg.png';
import bitcoinIcon from '@/assets/media/images/btc_icon_Platforma_Icons.svg';

const apiProxyOrigin = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '');
const apiBase = `${apiProxyOrigin}/api/creator-payments`;

const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const showStripeIntegrationLink = ref(false);
const accounts = ref([]);
const stripePlanAccess = ref({ enforced: false, allowed: true });
const showLimitPrompt = ref(false);
const limitResource = ref({});
const form = reactive({
  defaultProvider: 'platforma_payments',
  defaultAccountId: 0,
  platformaProcessorMode: 'stripe',
  platformaPaymentsEnabled: true,
  allowCreatorStripe: false,
  allowBitcoin: false,
});

const summaryPayments = reactive({
  totalPayments: 0,
  grossReceivedCents: 0,
  platformFeeCents: 0,
  netPayoutCents: 0,
});

const money = (valueCents) => {
  const cents = Number(valueCents || 0);
  return `$${(cents / 100).toFixed(2)}`;
};

const filteredAccountsForProvider = computed(() =>
  accounts.value.filter((account) => account.provider === form.defaultProvider)
);

const platformaActive = computed(() => accounts.value.some((account) => account.provider === 'platforma_payments' && account.status === 'active'));
const activeStripeAccounts = computed(() => accounts.value.filter((account) => account.provider === 'creator_stripe' && account.status === 'active'));
const stripePlanAllowed = computed(() => !stripePlanAccess.value?.enforced || Boolean(stripePlanAccess.value?.allowed));
const stripeActive = computed(() => activeStripeAccounts.value.length > 0);
const stripeEnabled = computed(() => stripePlanAllowed.value && stripeActive.value);
const selectionMessage = computed(() => {
  if (form.defaultProvider === 'creator_stripe') {
    const account = activeStripeAccounts.value.find((item) => Number(item.id) === Number(form.defaultAccountId)) || activeStripeAccounts.value[0];
    return `Checkout is set to ${account?.displayName || 'your connected Stripe account'}.`;
  }
  return 'Checkout is set to Platforma Payments.';
});

const StatusBadge = defineComponent({
  props: { active: Boolean, activeLabel: { type: String, default: '' }, inactiveLabel: { type: String, default: '' } },
  setup: (props) => () => h('span', { class: props.active ? 'inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200' : 'inline-flex rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500 ring-1 ring-inset ring-zinc-200' }, props.active ? props.activeLabel : props.inactiveLabel),
});

const summaryMetrics = computed(() => [
  { label: 'Total payments', value: summaryPayments.totalPayments, detail: 'Recorded member transactions', icon: ReceiptPercentIcon },
  { label: 'Gross received', value: money(summaryPayments.grossReceivedCents), detail: 'Before fees and adjustments', icon: BanknotesIcon },
  { label: 'Platform fees', value: money(summaryPayments.platformFeeCents), detail: 'Applied Platforma fees', icon: CreditCardIcon },
  { label: 'Estimated net', value: money(summaryPayments.netPayoutCents), detail: 'Before completed disbursements', icon: WalletIcon },
]);

const hydrateFromPayload = (payload = {}) => {
  const nextSettings = payload.settings || {};
  const nextAccounts = Array.isArray(payload.accounts) ? payload.accounts : [];
  const summary = payload.summary?.payments || {};

  accounts.value = nextAccounts;
  stripePlanAccess.value = payload.stripePlanAccess || { enforced: false, allowed: true };
  form.defaultProvider = String(nextSettings.defaultProvider || 'platforma_payments');
  form.defaultAccountId = Number(nextSettings.defaultAccountId || 0);
  form.platformaProcessorMode = String(nextSettings.platformaProcessorMode || 'stripe');
  form.platformaPaymentsEnabled = Boolean(nextSettings.platformaPaymentsEnabled ?? true);
  form.allowCreatorStripe = Boolean(nextSettings.allowCreatorStripe ?? false);
  form.allowBitcoin = Boolean(nextSettings.allowBitcoin ?? false);

  summaryPayments.totalPayments = Number(summary.totalPayments || 0);
  summaryPayments.grossReceivedCents = Number(summary.grossReceivedCents || 0);
  summaryPayments.platformFeeCents = Number(summary.platformFeeCents || 0);
  summaryPayments.netPayoutCents = Number(summary.netPayoutCents || 0);

  if (!form.defaultAccountId && filteredAccountsForProvider.value.length) {
    form.defaultAccountId = Number(filteredAccountsForProvider.value[0].id || 0);
  }
};

const providerCardClass = (provider) => {
  const selected = form.defaultProvider === provider;
  const ready = provider === 'platforma_payments' ? platformaActive.value : stripeActive.value && stripePlanAllowed.value;
  if (selected && ready) return 'border-emerald-400 bg-emerald-50/30 shadow-sm';
  if (provider === 'creator_stripe' && !stripePlanAllowed.value) return 'border-zinc-200 bg-zinc-50 opacity-75 hover:border-zinc-300';
  return selected ? 'border-[#2D8CFF] bg-blue-50/30 shadow-sm' : 'border-zinc-200 bg-white hover:border-blue-300 hover:shadow-sm';
};

const openStripeUpgrade = (details = {}) => {
  limitResource.value = {
    key: 'creator_stripe_accounts',
    label: 'connected Stripe accounts',
    state: 'blocked',
    message: 'Your current plan includes Platforma Payments. Upgrade to connect and route checkout payments through your own Stripe account.',
    ...details,
  };
  showLimitPrompt.value = true;
};

const selectProvider = (provider) => {
  errorMessage.value = '';
  showStripeIntegrationLink.value = false;
  if (provider === 'creator_stripe') {
    if (!stripePlanAllowed.value) return openStripeUpgrade();
    if (!stripeActive.value) {
      errorMessage.value = 'Connect a Stripe account from Integrations before selecting it for checkout.';
      showStripeIntegrationLink.value = true;
      return;
    }
    form.defaultProvider = 'creator_stripe';
    const account = activeStripeAccounts.value.find((item) => item.isDefault) || activeStripeAccounts.value[0];
    form.defaultAccountId = Number(account?.id || 0);
    form.allowCreatorStripe = true;
    return;
  }
  form.defaultProvider = 'platforma_payments';
  const account = accounts.value.find((item) => item.provider === 'platforma_payments' && item.status === 'active');
  form.defaultAccountId = Number(account?.id || 0);
  form.platformaPaymentsEnabled = true;
  form.allowCreatorStripe = stripeEnabled.value;
  form.allowBitcoin = false;
};

const fetchOverview = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  showStripeIntegrationLink.value = false;
  try {
    const response = await fetch(`${apiBase}/creator/overview`, {
      method: 'GET',
      credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(String(payload.message || 'Failed to load payments overview'));
    }
    hydrateFromPayload(payload);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to load payments overview');
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  errorMessage.value = '';
  showStripeIntegrationLink.value = false;
  try {
    form.platformaPaymentsEnabled = true;
    form.allowCreatorStripe = stripeEnabled.value;
    form.allowBitcoin = false;
    const response = await fetch(`${apiBase}/creator/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        defaultProvider: form.defaultProvider,
        defaultAccountId: Number(form.defaultAccountId || 0),
        platformaProcessorMode: form.platformaProcessorMode,
        platformaPaymentsEnabled: Boolean(form.platformaPaymentsEnabled),
        allowCreatorStripe: Boolean(form.allowCreatorStripe),
        allowBitcoin: Boolean(form.allowBitcoin),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (payload?.code === 'creator_plan_limit_reached') {
        openStripeUpgrade(payload.details || {});
        return;
      }
      throw new Error(String(payload.message || 'Failed to save payment settings'));
    }

    const followUp = await fetch(`${apiBase}/creator/overview`, {
      method: 'GET',
      credentials: 'include',
    });
    const nextPayload = await followUp.json().catch(() => ({}));
    if (!followUp.ok) {
      throw new Error(String(nextPayload.message || 'Settings saved but refresh failed'));
    }
    hydrateFromPayload(nextPayload);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to save payment settings');
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchOverview);
</script>
